import { z } from 'zod';
import { addDays, dateString, timeString, weekStart } from './training.js';

/**
 * Seasons: dates, phases, a focus for each week, regular training times,
 * and games. Weeks start on Monday.
 */

const name = (label: string, max = 60) => z.string().trim().min(1, `Give the ${label} a name.`).max(max, `Keep the ${label} name under ${max} characters.`);
const optText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `Keep the ${label} under ${max} characters.`)
    .nullish()
    .transform((v) => v || null);

export const phase = z.object({
  id: z.string().min(1).max(40),
  name: name('phase', 40),
  start: dateString,
  end: dateString,
});
export type Phase = z.infer<typeof phase>;

export const seasonWeek = z.object({
  weekStart: dateString,
  focus: optText(200, 'week’s focus'),
  tags: z.array(z.string().trim().min(1).max(30)).max(6).default([]),
});
export type SeasonWeek = z.infer<typeof seasonWeek>;

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

export const trainingSlot = z.object({
  id: z.string().min(1).max(40),
  /** 1 = Monday … 7 = Sunday */
  day: z.number().int().min(1, 'Choose a day.').max(7, 'Choose a day.'),
  startTime: timeString,
  durationMinutes: z.coerce.number().int().min(10, 'A session needs at least 10 minutes.').max(360, 'Keep a session under 6 hours.'),
  location: optText(120, 'location'),
  templateId: z.string().uuid().nullish().transform((v) => v ?? null),
});
export type TrainingSlot = z.infer<typeof trainingSlot>;

const seasonFields = {
  name: name('season'),
  startDate: dateString,
  endDate: dateString,
};
const rangeOk = (s: { startDate?: string; endDate?: string }) => !s.startDate || !s.endDate || s.startDate <= s.endDate;
const lengthOk = (s: { startDate?: string; endDate?: string }) =>
  !s.startDate || !s.endDate || (Date.parse(s.endDate) - Date.parse(s.startDate)) / 86400000 <= 400;

export const createSeasonInput = z
  .object(seasonFields)
  .refine(rangeOk, { message: 'The season has to end after it starts.', path: ['endDate'] })
  .refine(lengthOk, { message: 'Keep a season under about 13 months.', path: ['endDate'] });
export type CreateSeasonInput = z.input<typeof createSeasonInput>;

export const updateSeasonInput = z
  .object({
    name: seasonFields.name.optional(),
    startDate: dateString.optional(),
    endDate: dateString.optional(),
    phases: z.array(phase).max(12, 'Use up to 12 phases.').optional(),
    weeks: z.array(seasonWeek).max(60).optional(),
    training: z.array(trainingSlot).max(14, 'Use up to 14 regular training times.').optional(),
  })
  .refine(rangeOk, { message: 'The season has to end after it starts.', path: ['endDate'] })
  .refine(lengthOk, { message: 'Keep a season under about 13 months.', path: ['endDate'] });
export type UpdateSeasonInput = z.input<typeof updateSeasonInput>;

export const generateSessionsInput = z.object({
  /** Only create sessions from this date on (usually today). */
  from: dateString,
  /** Count what would be created without creating anything. */
  dryRun: z.boolean().default(false),
});

export const GAME_VENUES = ['home', 'away', 'neutral'] as const;
export type GameVenue = (typeof GAME_VENUES)[number];
export const VENUE_LABELS: Record<GameVenue, string> = { home: 'Home', away: 'Away', neutral: 'Neutral' };

export const gameInput = z.object({
  date: dateString,
  startTime: timeString.nullish().transform((v) => v ?? null),
  opponent: z.string().trim().min(1, 'Enter who you’re playing.').max(80, 'Keep the opponent’s name under 80 characters.'),
  venue: z.enum(GAME_VENUES).default('home'),
  location: optText(120, 'location'),
  notes: optText(1000, 'notes'),
});
export type GameInput = z.input<typeof gameInput>;

export interface Game {
  id: string;
  teamId: string;
  teamName: string;
  date: string;
  startTime: string | null;
  opponent: string;
  venue: GameVenue;
  location: string | null;
  notes: string | null;
  /** Imported from PlayHQ: kept in sync and read-only apart from notes. */
  fromPlayhq: boolean;
  round: string | null;
  scoreUs: number | null;
  scoreThem: number | null;
  /** For us: WIN, LOSS, DRAW (PlayHQ's words), once the game's done. */
  outcome: string | null;
  /** PlayHQ's status, e.g. UPCOMING, COMPLETED, FORFEIT, CANCELLED. */
  playhqStatus: string | null;
}

export interface SeasonSummary {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

export interface SeasonWeekView {
  weekStart: string;
  phase: { id: string; name: string } | null;
  focus: string | null;
  tags: string[];
  sessions: { id: string; date: string; title: string; plannedMinutes: number; status: 'draft' | 'published' }[];
  games: Game[];
}

export interface Coverage {
  /** Minutes of drills, counting each group when drills run at the same time. */
  totalMinutes: number;
  byTag: { tag: string; minutes: number }[];
  untaggedMinutes: number;
  /** Sessions with at least one drill. */
  sessionCount: number;
}

export interface SeasonDetail extends SeasonSummary {
  teamId: string;
  phases: Phase[];
  training: TrainingSlot[];
  weeks: SeasonWeekView[];
  coverage: Coverage;
  canEdit: boolean;
}

/** Every Monday from the week containing `start` to the week containing `end`. */
export function seasonWeeks(start: string, end: string): string[] {
  const out: string[] = [];
  for (let w = weekStart(start); w <= end && out.length < 60; w = addDays(w, 7)) out.push(w);
  return out;
}

/** Sensible starting phases: pre-season, competition, finals. */
export function defaultPhases(start: string, end: string, newId: () => string): Phase[] {
  const days = Math.round((Date.parse(end) - Date.parse(start)) / 86400000) + 1;
  if (days < 56) return [{ id: newId(), name: 'Competition', start, end }];
  const preEnd = addDays(start, Math.min(28, Math.round(days * 0.2)) - 1);
  const finalsStart = addDays(end, -13);
  return [
    { id: newId(), name: 'Pre-season', start, end: preEnd },
    { id: newId(), name: 'Competition', start: addDays(preEnd, 1), end: addDays(finalsStart, -1) },
    { id: newId(), name: 'Finals', start: finalsStart, end },
  ];
}
